import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Grid, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, TextField,
  Autocomplete, IconButton, Alert, Snackbar, CircularProgress, Divider,
  Chip, Stack, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { Trash2, Save, TrendingUp, Receipt, Package, DollarSign, Pencil, Download } from 'lucide-react';
import { subscribeToProducts } from '../firebase/productService';
import { getExpensesByDateRange } from '../firebase/expenseService';
import {
  getMonthlyPnL, saveMonthlyPnL, deleteMonthlyPnL, computeProductLine, computeClosingStock, computeTotals, listMonthlyPnLHistory,
} from '../firebase/monthlyPnLService';
import { useBranch } from '../contexts/BranchContext';

const fmt = (n) => (n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const currentMonthId = () => new Date().toISOString().slice(0, 7);

// Date range for either a full calendar month, or a half-month period (1st-15th / 16th-end)
const periodDateRange = (monthId, periodType, half) => {
  const [y, m] = monthId.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  if (periodType === 'semi') {
    return half === 'H1'
      ? { start: `${monthId}-01`, end: `${monthId}-15` }
      : { start: `${monthId}-16`, end: `${monthId}-${String(lastDay).padStart(2, '0')}` };
  }
  return { start: `${monthId}-01`, end: `${monthId}-${String(lastDay).padStart(2, '0')}` };
};

const periodLabel = (periodType, half, monthId) => {
  if (periodType !== 'semi') return monthId;
  return `${monthId} (${half === 'H1' ? '1st Half' : '2nd Half'})`;
};

const csvEscape = (value) => {
  const s = String(value ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (filename, rows) => {
  const csv = rows.map((row) => row.map(csvEscape).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const MonthlyPnL = ({ currentUser, permissions }) => {
  // Monthly P&L reveals cost, margin and profit data — strictly admin-only, never a togglable staff permission.
  const isAdmin = permissions?.userType === 'admin';
  const { activeBranch, loading: branchLoading } = useBranch();

  const [monthId, setMonthId] = useState(currentMonthId());
  const [periodType, setPeriodType] = useState('monthly'); // 'monthly' | 'semi'
  const [half, setHalf] = useState('H1'); // 'H1' | 'H2' — only used when periodType === 'semi'
  const [products, setProducts] = useState([]);
  const [revenue, setRevenue] = useState('');
  const [productLines, setProductLines] = useState([]);
  const [expensesTotal, setExpensesTotal] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openingInput, setOpeningInput] = useState('');
  const [inBetweenInput, setInBetweenInput] = useState('');
  const [returnInput, setReturnInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [recordExists, setRecordExists] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [trendProduct, setTrendProduct] = useState(null);

  const periodId = periodType === 'semi' ? `${monthId}-${half}` : monthId;

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  useEffect(() => {
    const unsub = subscribeToProducts(setProducts);
    return () => unsub();
  }, []);

  const loadMonth = useCallback(async (id, branchId, start, end) => {
    if (!branchId) {
      setRevenue('');
      setProductLines([]);
      setExpensesTotal(0);
      setRecordExists(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    const [pnlRes, expRes] = await Promise.all([
      getMonthlyPnL(`${branchId}_${id}`),
      getExpensesByDateRange(start, end),
    ]);

    if (pnlRes.success) {
      setRevenue((pnlRes.data.revenue ?? '').toString());
      setProductLines(pnlRes.data.productLines || []);
      setRecordExists(true);
    } else {
      setRevenue('');
      setProductLines([]);
      setRecordExists(false);
    }

    if (expRes.success) {
      setExpensesTotal(
        expRes.data
          .filter((e) => e.branchId === branchId)
          .reduce((s, e) => s + (e.grandTotal ?? e.amount ?? 0), 0)
      );
    } else {
      setExpensesTotal(0);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const { start, end } = periodDateRange(monthId, periodType, half);
    loadMonth(periodId, activeBranch?.id, start, end);
  }, [periodId, monthId, periodType, half, activeBranch, loadMonth]);

  useEffect(() => {
    if (!activeBranch?.id) {
      setHistory([]);
      return;
    }
    setHistoryLoading(true);
    listMonthlyPnLHistory(100).then((res) => {
      if (res.success) {
        setHistory(res.data.filter((d) => d.branchId === activeBranch.id));
      } else {
        setHistory([]);
      }
      setHistoryLoading(false);
    });
  }, [activeBranch]);

  const productTrend = useMemo(() => {
    if (!trendProduct) return [];
    return history
      .map((monthDoc) => {
        const line = (monthDoc.productLines || []).find((l) => l.productId === trendProduct.id);
        if (!line) return null;
        return { month: monthDoc.month, ...line };
      })
      .filter(Boolean)
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [history, trendProduct]);

  const totals = useMemo(
    () => computeTotals({ revenue, productLines, expensesTotal }),
    [revenue, productLines, expensesTotal]
  );

  const closingPreview = computeClosingStock({
    openingStock: openingInput, inBetweenStock: inBetweenInput, returnGoods: returnInput,
  });

  const addProductLine = () => {
    if (!selectedProduct) {
      showSnackbar('Select a product', 'error');
      return;
    }
    const line = computeProductLine({
      product: selectedProduct,
      openingStock: openingInput,
      inBetweenStock: inBetweenInput,
      returnGoods: returnInput,
    });
    setProductLines((prev) => {
      const idx = prev.findIndex((l) => l.productId === line.productId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = line;
        return next;
      }
      return [...prev, line];
    });
    setSelectedProduct(null);
    setOpeningInput('');
    setInBetweenInput('');
    setReturnInput('');
  };

  const removeLine = (productId) => {
    setProductLines((prev) => prev.filter((l) => l.productId !== productId));
  };

  const editLine = (line) => {
    const product = products.find((p) => p.id === line.productId);
    setSelectedProduct(product || { id: line.productId, name: line.name, nameTa: line.nameTa, costPrice: line.costPrice, sellingPrice: line.sellingPrice });
    setOpeningInput(String(line.openingStock ?? ''));
    setInBetweenInput(String(line.inBetweenStock ?? ''));
    setReturnInput(String(line.returnGoods ?? ''));
  };

  const refreshHistory = useCallback(async () => {
    if (!activeBranch?.id) return;
    const res = await listMonthlyPnLHistory(100);
    if (res.success) setHistory(res.data.filter((d) => d.branchId === activeBranch.id));
  }, [activeBranch]);

  const handleSave = async () => {
    // Revenue is intentionally optional — the client fills in Opening Stock at
    // the start of the month and saves, then comes back at month-end to add
    // In Between Stock, Return Goods and Revenue once they're known.
    if (!activeBranch) {
      showSnackbar('Select a branch first', 'error');
      return;
    }
    setSaving(true);
    const payload = {
      branchId: activeBranch.id,
      periodType,
      half: periodType === 'semi' ? half : null,
      revenue: parseFloat(revenue) || 0,
      productLines,
      expensesTotal,
      ...totals,
      createdBy: currentUser?.uid || null,
      createdByName: currentUser?.displayName || currentUser?.email || null,
    };
    const result = await saveMonthlyPnL(`${activeBranch.id}_${periodId}`, payload);
    setSaving(false);
    if (result.success) {
      showSnackbar('Monthly P&L saved');
      setRecordExists(true);
      refreshHistory();
    } else {
      showSnackbar(result.error || 'Failed to save', 'error');
    }
  };

  const handleDelete = async () => {
    if (!activeBranch) return;
    if (!window.confirm(`Delete the saved P&L record for ${periodLabel(periodType, half, monthId)}? This cannot be undone.`)) return;
    setDeleting(true);
    const result = await deleteMonthlyPnL(`${activeBranch.id}_${periodId}`);
    setDeleting(false);
    if (result.success) {
      showSnackbar('Monthly P&L record deleted');
      setRevenue('');
      setProductLines([]);
      setRecordExists(false);
      refreshHistory();
    } else {
      showSnackbar(result.error || 'Failed to delete', 'error');
    }
  };

  const handleExportCsv = () => {
    const rows = [
      ['Roshan Gram Store — Monthly P&L'],
      ['Branch', activeBranch?.name || ''],
      ['Period', periodLabel(periodType, half, monthId)],
      [],
      ['Product', 'Tamil Name', 'Opening Stock', 'In Between Stock', 'Return Goods', 'Closing Stock (Sold Goods)', 'Cost Price', 'Retail Price', 'Total Cost Value', 'Retail Total Value', 'Margin %'],
      ...productLines.map((l) => [
        l.name, l.nameTa, l.openingStock ?? '', l.inBetweenStock ?? '', l.returnGoods ?? '',
        l.qtySold, l.costPrice, l.sellingPrice, l.lineCost, l.lineRevenue, l.marginPct.toFixed(1),
      ]),
      [],
      ['Total Revenue', parseFloat(revenue) || 0],
      ['Total COGS', totals.totalCOGS],
      ['Gross Profit', totals.grossProfit],
      ['Expenses', expensesTotal],
      ['Net Profit', totals.netProfit],
    ];
    downloadCsv(`PnL_${(activeBranch?.name || 'branch').replace(/\s+/g, '_')}_${periodId}.csv`, rows);
  };

  const StatCard = ({ icon, label, value, color = 'primary' }) => (
    <Card>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: `${color}.main`, color: '#ffffff', display: 'flex' }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700}>{value}</Typography>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );

  if (!isAdmin) {
    return <Alert severity="warning">You don't have access to Monthly P&L.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Monthly P&L</Typography>
          <Typography variant="body2" color="text.secondary">
            {activeBranch ? `${activeBranch.name} — ` : ''}Enter revenue and product sales to compute profit &amp; loss — admin only
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <ToggleButtonGroup
            size="small" value={periodType} exclusive
            onChange={(_, v) => v && setPeriodType(v)}
          >
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="semi">Semi-monthly</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            type="month" size="small" label="Month"
            value={monthId} onChange={(e) => setMonthId(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          {periodType === 'semi' && (
            <ToggleButtonGroup
              size="small" value={half} exclusive
              onChange={(_, v) => v && setHalf(v)}
            >
              <ToggleButton value="H1">1st Half (1–15)</ToggleButton>
              <ToggleButton value="H2">2nd Half (16–end)</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Stack>
      </Box>

      {!activeBranch && !branchLoading && (
        <Alert severity="warning" sx={{ mb: 3 }}>No branch selected. Choose a branch from the header first.</Alert>
      )}

      {(loading || branchLoading) ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <TextField
              label={`Total Revenue for ${periodLabel(periodType, half, monthId)} (LKR)`} type="number" value={revenue}
              onChange={(e) => setRevenue(e.target.value)}
              fullWidth sx={{ maxWidth: 320 }}
            />
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Products Sold This Month</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Opening Stock + In Between Stock − Return Goods = Closing Stock (Sold Goods). Retail Price × Sold Goods = Retail Total Value. Cost Price × Sold Goods = Total Cost Value.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }} alignItems="flex-start">
              <Autocomplete
                sx={{ minWidth: 240, flexGrow: 1 }}
                options={products}
                value={selectedProduct}
                onChange={(_, v) => setSelectedProduct(v)}
                getOptionLabel={(p) => p.nameTa ? `${p.name} (${p.nameTa})` : p.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => <TextField {...params} label="Product" size="small" />}
              />
              <TextField
                label="Opening Stock" type="number" size="small" value={openingInput}
                onChange={(e) => setOpeningInput(e.target.value)}
                sx={{ width: 130 }}
              />
              <TextField
                label="In Between Stock" type="number" size="small" value={inBetweenInput}
                onChange={(e) => setInBetweenInput(e.target.value)}
                sx={{ width: 140 }}
              />
              <TextField
                label="Return Goods" type="number" size="small" value={returnInput}
                onChange={(e) => setReturnInput(e.target.value)}
                sx={{ width: 130 }}
              />
              <Button variant="contained" onClick={addProductLine} sx={{ mt: 0.5 }}>Add</Button>
            </Stack>
            {selectedProduct && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Closing Stock (Sold Goods) for this line: <strong>{closingPreview}</strong>
              </Typography>
            )}

            <TableContainer variant="outlined" component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tamil Name</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Opening</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>In Between</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Return</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Sold Goods</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Cost Price</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Retail Price</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Total Cost Value</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Retail Total Value</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Margin %</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productLines.map((line) => (
                    <TableRow key={line.productId} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{line.name}</TableCell>
                      <TableCell>{line.nameTa}</TableCell>
                      <TableCell align="right">{line.openingStock ?? 0}</TableCell>
                      <TableCell align="right">{line.inBetweenStock ?? 0}</TableCell>
                      <TableCell align="right">{line.returnGoods ?? 0}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{line.qtySold}</TableCell>
                      <TableCell align="right">{fmt(line.costPrice)}</TableCell>
                      <TableCell align="right">{fmt(line.sellingPrice)}</TableCell>
                      <TableCell align="right">{fmt(line.lineCost)}</TableCell>
                      <TableCell align="right">{fmt(line.lineRevenue)}</TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          label={`${line.marginPct.toFixed(1)}%`}
                          color={line.marginPct >= 20 ? 'success' : line.marginPct >= 0 ? 'warning' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary" onClick={() => editLine(line)}>
                          <Pencil size={16} />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => removeLine(line.productId)}>
                          <Trash2 size={16} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {productLines.length === 0 && (
                    <TableRow><TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">No products added yet</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Product Performance Trend</Typography>
            <Autocomplete
              sx={{ minWidth: 280, maxWidth: 400, mb: 2 }}
              options={products}
              value={trendProduct}
              onChange={(_, v) => setTrendProduct(v)}
              getOptionLabel={(p) => p.nameTa ? `${p.name} (${p.nameTa})` : p.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label="Select a product to check its performance" size="small" />}
            />

            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
            ) : !trendProduct ? (
              <Typography color="text.secondary">Pick a product above to see how it has performed across months.</Typography>
            ) : productTrend.length === 0 ? (
              <Typography color="text.secondary">No saved monthly data for this product yet.</Typography>
            ) : (
              <TableContainer variant="outlined" component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Month</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Qty Sold</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Line Cost</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Line Revenue</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Margin %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productTrend.map((row) => (
                      <TableRow key={row.month} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{row.month}</TableCell>
                        <TableCell align="right">{row.qtySold}</TableCell>
                        <TableCell align="right">{fmt(row.lineCost)}</TableCell>
                        <TableCell align="right">{fmt(row.lineRevenue)}</TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={`${row.marginPct.toFixed(1)}%`}
                            color={row.marginPct >= 20 ? 'success' : row.marginPct >= 0 ? 'warning' : 'error'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}><StatCard icon={<Package size={20} />} label="Total COGS" value={`LKR ${fmt(totals.totalCOGS)}`} color="text.secondary" /></Grid>
            <Grid item xs={6} sm={3}><StatCard icon={<TrendingUp size={20} />} label="Gross Profit" value={`LKR ${fmt(totals.grossProfit)}`} color="success" /></Grid>
            <Grid item xs={6} sm={3}><StatCard icon={<Receipt size={20} />} label="Expenses (from Expenses module)" value={`LKR ${fmt(expensesTotal)}`} color="warning" /></Grid>
            <Grid item xs={6} sm={3}><StatCard icon={<DollarSign size={20} />} label="Net Profit" value={`LKR ${fmt(totals.netProfit)}`} color={totals.netProfit >= 0 ? 'success' : 'error'} /></Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined" startIcon={<Download size={18} />}
              onClick={handleExportCsv}
            >
              Export CSV
            </Button>
            {recordExists && (
              <Button
                variant="outlined" color="error"
                startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Trash2 size={18} />}
                onClick={handleDelete} disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Record'}
              </Button>
            )}
            <Button
              variant="contained" size="large" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save size={18} />}
              onClick={handleSave} disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Monthly P&L'}
            </Button>
          </Box>
        </>
      )}

      <Snackbar
        open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MonthlyPnL;
